/**
 * EntryPointTabs.jsx
 *
 * **NEW component — no legacy source.** Brainstorm-required improvement
 * (brainstorm DTH section "Vandana's corrections + additions" #1 + Q4 +
 * wishlist #1, all locked 19 May 2026).
 *
 * Visually-distinct affordance for the two DTH entry points (DTH-only).
 * Legacy `searchResults.js:1450-1706` supports both via the `reverseFilter`
 * flag but HIDES the affordance — the user must hover-discover that they can
 * start with either a hole size OR a Hammer Series. SP3 surfaces both
 * explicitly as a two-card row above the slot cards.
 *
 * Two entry points (cascade behaviour ported from legacy `reverseFilter`
 * flag; the PWA cascade `services/dscCascade.js:383` INFERS the flag from
 * `state.selections['hammer-series']?.family`):
 *   1. **Hole-size-first** (`reverseFilter=false`, default) — user picks hole
 *      size first; cascade builds hammer options + shank size.
 *   2. **Hammer-Series-first** (`reverseFilter=true`) — user picks the Hammer
 *      Series first; cascade reverse-derives bit diameters + pin options.
 *
 * Citations (brainstorm-locked):
 *  - Brainstorm DTH #1 (lines 156-157) — "Two entry points are NOT visually
 *    distinguished today ... Fix: surface this as an explicit affordance."
 *  - Brainstorm DTH live capture (lines 151-153) — entry-point semantics +
 *    `reverseFilter` lineage.
 *  - Brainstorm Q4 (lines 224-228) — TWO entry points supported, make both
 *    visually obvious.
 *  - Brainstorm wishlist #1 (line 283).
 *  - Plan §SP3.2 — visual two-tab spec + helper-line copy locked.
 *  - Plan §1.5 + brainstorm Q5 — switching entry point mid-config preserves
 *    compatible selections (no full reset).
 *
 * State plumbing:
 *  - Controlled `value` / `onChange` pair; `DTHDSCLayout` hosts the local
 *    `entryPoint` useState. We did NOT lift this into `DSCContext` — see
 *    the SP3 hand-off report rationale (cascade reducer infers
 *    `reverseFilter` independently).
 *  - Flipping this UI tab without picking anything yet has no cascade
 *    effect — the brainstorm-locked "no full reset" behaviour.
 *
 * Visual rules (Sandvik PWA template):
 *  - Two equal-width cards (`lg:flex-1`), electric-blue active fill, no
 *    rounded corners (`rounded-none`), font-sandvik-text, focus ring, hover.
 *  - Hammer + Drill icons from `lucide-react` (already in package.json).
 *  - Stacks vertically on mobile (`flex-col lg:flex-row`) — brainstorm DTH
 *    wishlist #5 vertical-layout direction.
 *
 * @module components/dsc/EntryPointTabs
 * @since 2026-05-20 (Group E SP3 — DTH-specific components; NEW, no legacy
 *   source — brainstorm-required improvement)
 */

import { Hammer, Drill } from 'lucide-react';

export const DTH_ENTRY_HOLE_SIZE_FIRST = 'hole-size-first';
export const DTH_ENTRY_HAMMER_FIRST = 'hammer-first';

/**
 * @param {Object} props
 * @param {'hole-size-first'|'hammer-first'} [props.value]  controlled entry
 *        point. Defaults to 'hole-size-first' if not provided.
 * @param {(next: 'hole-size-first'|'hammer-first') => void} [props.onChange]
 * @param {string} [props.className]
 * @param {string} [props.dataTestid]
 */
export default function EntryPointTabs({
  value = DTH_ENTRY_HOLE_SIZE_FIRST,
  onChange,
  className = '',
  dataTestid = 'entry-point-tabs',
}) {
  const handleSelect = (next) => {
    if (next === value) return;
    if (typeof onChange === 'function') onChange(next);
  };

  const cardBase =
    'flex flex-col items-start text-left gap-2 px-5 py-4 border focus:outline-none ' +
    'focus:ring-2 focus:ring-electric-blue focus:ring-offset-1 transition-colors ' +
    'rounded-none flex-1 min-w-0';
  const cardActive = 'bg-electric-blue text-white border-electric-blue';
  const cardInactive =
    'bg-bg-paper text-carbon-black border-n3 hover:border-electric-blue';

  const isHoleSize = value === DTH_ENTRY_HOLE_SIZE_FIRST;
  const isHammer = value === DTH_ENTRY_HAMMER_FIRST;

  return (
    <div
      role="tablist"
      aria-label="DTH entry point"
      data-testid={dataTestid}
      className={`flex flex-col gap-3 lg:flex-row ${className}`}
    >
      {/* Hole-size-first — the default entry point (legacy reverseFilter=false). */}
      <button
        type="button"
        role="tab"
        aria-selected={isHoleSize}
        data-testid="entry-point-hole-size-first"
        onClick={() => handleSelect(DTH_ENTRY_HOLE_SIZE_FIRST)}
        className={`${cardBase} ${isHoleSize ? cardActive : cardInactive}`}
      >
        <div className="flex items-center gap-3">
          <Drill
            size={28}
            aria-hidden="true"
            className={isHoleSize ? 'text-white' : 'text-electric-blue'}
          />
          <span className="text-base font-sandvik-headline font-normal">
            Start with the hole size
          </span>
        </div>
        <span
          className={`text-sm font-sandvik-text ${
            isHoleSize ? 'text-white/90' : 'text-n6'
          }`}
        >
          I know the hole size I want to drill
        </span>
      </button>

      {/* Hammer-Series-first — the legacy `reverseFilter=true` branch. */}
      <button
        type="button"
        role="tab"
        aria-selected={isHammer}
        data-testid="entry-point-hammer-first"
        onClick={() => handleSelect(DTH_ENTRY_HAMMER_FIRST)}
        className={`${cardBase} ${isHammer ? cardActive : cardInactive}`}
      >
        <div className="flex items-center gap-3">
          <Hammer
            size={28}
            aria-hidden="true"
            className={isHammer ? 'text-white' : 'text-electric-blue'}
          />
          <span className="text-base font-sandvik-headline font-normal">
            Start with the Hammer Series
          </span>
        </div>
        <span
          className={`text-sm font-sandvik-text ${
            isHammer ? 'text-white/90' : 'text-n6'
          }`}
        >
          I know the hammer series I'm using
        </span>
      </button>
    </div>
  );
}
